import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Plus, MapPin, Building2, Mail, Phone, Trash2, Package, ShieldCheck, ShieldAlert, Pencil } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { formatApiError } from "@/lib/api";
import ClientForm from "@/components/ClientForm";
import { useAuth } from "@/context/AuthContext";

export default function ClientDetail() {
  const { id } = useParams();
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin" || me?.role_name === "Admin";
  const [client, setClient] = useState(null);
  const [soldMachines, setSoldMachines] = useState([]);
  const [addrOpen, setAddrOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [addr, setAddr] = useState({ label: "", line: "", city: "", state: "", pincode: "" });

  const load = async () => {
    try {
      const [c, sm] = await Promise.all([api.get(`/clients/${id}`), api.get(`/clients/${id}/sold-machines`)]);
      setClient(c.data);
      setSoldMachines(sm.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  useEffect(() => { load(); }, [id]);

  const addAddress = async (e) => {
    e.preventDefault();
    try {
      const res = await api.post(`/clients/${id}/addresses`, addr);
      setClient(res.data);
      setAddr({ label: "", line: "", city: "", state: "", pincode: "" });
      setAddrOpen(false);
      toast.success("Site address added");
    } catch (e2) { toast.error(formatApiError(e2)); }
  };

  const removeAddr = async (addrId) => {
    try {
      const res = await api.delete(`/clients/${id}/addresses/${addrId}`);
      setClient(res.data);
      toast.success("Address removed");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!client) return <div className="text-slate-500">Loading...</div>;

  // Group machines by install site label
  const bySite = soldMachines.reduce((acc, m) => {
    const key = m.sold_to_address_label || "Unassigned";
    (acc[key] = acc[key] || []).push(m);
    return acc;
  }, {});

  return (
    <div data-testid="client-detail-page">
      <Link to="/clients" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4" data-testid="back-to-clients">
        <ArrowLeft className="w-4 h-4" /> Back to clients
      </Link>

      <div className="bg-white border border-slate-200 rounded-lg p-6 mb-6">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <div className="w-10 h-10 rounded-md bg-indigo-50 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <h1 className="font-heading text-2xl font-semibold text-slate-900" data-testid="client-company-name">{client.company_name}</h1>
                <div className="text-slate-500 text-sm">{client.contact_person}</div>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Badge variant="secondary" className="bg-indigo-50 text-indigo-700 border border-indigo-100 font-mono">GST · {client.gst_number}</Badge>
            {isAdmin && (
              <Dialog open={editOpen} onOpenChange={setEditOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" data-testid="edit-client-btn">
                    <Pencil className="w-3.5 h-3.5 mr-1.5" /> Edit
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl">
                  <DialogHeader>
                    <DialogTitle>Edit client</DialogTitle>
                  </DialogHeader>
                  <ClientForm
                    initial={client}
                    onCancel={() => setEditOpen(false)}
                    onSuccess={(updated) => { setClient(updated); setEditOpen(false); toast.success("Client updated"); }}
                  />
                </DialogContent>
              </Dialog>
            )}
          </div>
        </div>

        <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-6 pt-6 border-t border-slate-100">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Email</div>
            <div className="text-sm text-slate-900 flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-slate-400" />{client.email}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Phone</div>
            <div className="text-sm text-slate-900 flex items-center gap-1.5"><Phone className="w-3.5 h-3.5 text-slate-400" />{client.phone}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Lead source</div>
            <div className="text-sm text-slate-900">{client.lead_source || "—"}</div>
          </div>
          <div>
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Billing address</div>
            <div className="text-sm text-slate-900 whitespace-pre-line">{client.billing_address}</div>
          </div>
        </div>
      </div>

      <Tabs defaultValue="addresses">
        <TabsList className="bg-white border border-slate-200">
          <TabsTrigger value="addresses" data-testid="tab-addresses">Site addresses</TabsTrigger>
          <TabsTrigger value="machines" data-testid="tab-machines">Machines at this site ({soldMachines.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="addresses" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="font-heading text-lg font-semibold text-slate-900">Shipping / site addresses</h2>
                <p className="text-slate-500 text-sm">This client can have multiple sites — add each one with a clear label.</p>
              </div>
              <Dialog open={addrOpen} onOpenChange={setAddrOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-address-btn"><Plus className="w-4 h-4 mr-1.5" /> Add site</Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Add site address</DialogTitle></DialogHeader>
                  <form onSubmit={addAddress} className="space-y-3">
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Label *</Label>
                      <Input required placeholder='e.g. "Site A - Mumbai"' value={addr.label} onChange={(e) => setAddr({ ...addr, label: e.target.value })} data-testid="address-label-input" className="mt-1" />
                    </div>
                    <div>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Address line *</Label>
                      <Textarea required value={addr.line} onChange={(e) => setAddr({ ...addr, line: e.target.value })} data-testid="address-line-input" className="mt-1" rows={2} />
                    </div>
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">City</Label>
                        <Input value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} data-testid="address-city-input" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">State</Label>
                        <Input value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} data-testid="address-state-input" className="mt-1" />
                      </div>
                      <div>
                        <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Pincode</Label>
                        <Input value={addr.pincode} onChange={(e) => setAddr({ ...addr, pincode: e.target.value })} data-testid="address-pincode-input" className="mt-1" />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => setAddrOpen(false)}>Cancel</Button>
                      <Button type="submit" className="bg-indigo-600 hover:bg-indigo-700" data-testid="address-save-btn">Save address</Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {(client.shipping_addresses || []).length === 0 ? (
              <div className="text-center text-slate-400 py-10" data-testid="addresses-empty">
                <MapPin className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No site addresses yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {client.shipping_addresses.map((a) => (
                  <div key={a.id} className="border border-slate-200 rounded-md p-4 hover:border-indigo-200 transition-colors duration-150" data-testid={`address-card-${a.id}`}>
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-1.5">
                        <MapPin className="w-4 h-4 text-indigo-600" />
                        <div className="font-semibold text-slate-900 text-sm">{a.label}</div>
                      </div>
                      <button onClick={() => removeAddr(a.id)} data-testid={`remove-address-${a.id}`} className="text-slate-400 hover:text-red-600" aria-label="Remove address">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="text-sm text-slate-600 whitespace-pre-line">{a.line}</div>
                    <div className="text-xs text-slate-500 mt-1">
                      {[a.city, a.state, a.pincode].filter(Boolean).join(", ")}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="machines" className="mt-4">
          <div className="bg-white border border-slate-200 rounded-lg p-6" data-testid="sold-machines-tab">
            <div className="mb-4">
              <h2 className="font-heading text-lg font-semibold text-slate-900">Machines sold to this client</h2>
              <p className="text-slate-500 text-sm">Grouped by install site. Track warranty / next AMC visit.</p>
            </div>
            {soldMachines.length === 0 ? (
              <div className="text-center text-slate-400 py-10" data-testid="sold-machines-empty">
                <Package className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                No sold machines yet.
              </div>
            ) : (
              <div className="space-y-6">
                {Object.entries(bySite).map(([site, machines]) => (
                  <div key={site} data-testid={`site-group-${site.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="w-4 h-4 text-indigo-600" />
                      <div className="font-semibold text-slate-900 text-sm">{site}</div>
                      <span className="text-xs text-slate-500 bg-slate-100 rounded px-2 py-0.5">{machines.length}</span>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {machines.map((m) => <MachineCard key={m.id} m={m} />)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function MachineCard({ m }) {
  const amcOverdue = m.amc_overdue;
  const amcSoon = !amcOverdue && typeof m.days_until_next_amc === "number" && m.days_until_next_amc <= 30;
  return (
    <div className="border border-slate-200 rounded-md p-4 hover:border-indigo-200 transition-colors duration-150" data-testid={`sold-machine-${m.id}`}>
      <div className="flex items-start justify-between mb-2">
        <div className="min-w-0">
          <div className="font-semibold text-slate-900 text-sm truncate">{m.catalog?.model_name || "—"}</div>
          <div className="text-xs text-slate-500">{m.catalog?.manufacturer}</div>
        </div>
        {(amcOverdue || amcSoon) && (
          <Badge className={amcOverdue ? "bg-red-50 text-red-700 border border-red-200" : "bg-amber-50 text-amber-700 border border-amber-200"} variant="outline">
            {amcOverdue ? <ShieldAlert className="w-3 h-3 mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
            {amcOverdue ? "AMC overdue" : "AMC soon"}
          </Badge>
        )}
      </div>
      <div className="text-xs text-slate-600 space-y-0.5">
        <div className="font-mono">{m.serial_number}{m.nickname && <span className="ml-2 text-indigo-600">· {m.nickname}</span>}</div>
        <div>Sold: {m.sold_date || "—"}</div>
        {m.next_amc_date && (
          <div className={amcOverdue ? "text-red-700 font-semibold" : "text-slate-700"} data-testid={`amc-countdown-${m.id}`}>
            Next AMC: {m.next_amc_date} · {m.days_until_next_amc >= 0 ? `${m.days_until_next_amc} days` : `${Math.abs(m.days_until_next_amc)} days overdue`}
          </div>
        )}
      </div>
    </div>
  );
}
